import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { arrayOf, func, shape, string } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import { renderInput } from '../utils/renderUtils';
import { useGetTerminalsQuery } from '../customerAuthorizationsEndpoints';

const Terminals = ({ info, setInfo }) => {
  const { currentAffiliate = {} } = useUserRolePermissions();
  const { controlType, customerIdName, terminals = [] } = info;
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

  const isNotSinglePoint = R.not(R.equals('SINGLE', controlType));
  const terminalLength = R.length(terminals);

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
  };

  const { data: terminalsQuery = [], isFetching: isFetchingTerminals } = useGetTerminalsQuery(
    {
      params,
    },
    { skip: R.or(R.isEmpty(customerIdName), R.equals('SINGLE', controlType)) }
  );

  const handleChange = (_, value, reason, detail) => {
    if (reason === 'removeOption') {
      const { option } = detail;
      const filtered = terminals.filter(({ terminalId }) => terminalId !== option.terminalId);
      // as terminals from edit customer auth are a diff obj from get terminals
      // if you try to remove one that was previously selected from edit customer
      // then value will be [] due to not exact matching the list
      const newTerminals = R.equals(value, []) ? filtered : value;
      setInfo(R.assoc('terminals', newTerminals, info));
    } else {
      setInfo(R.assoc('terminals', value, info));
    }
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        aria-controls="customer-customerAuth-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">Terminals</Typography>
          {!!terminalLength && isNotSinglePoint && (
            <Chip label={terminalLength} type="accordion" variant="badge" />
          )}
          {R.isEmpty(terminals) && (
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <ErrorRoundedIcon sx={{ color: 'error.main', mr: 0.5 }} />
              <Typography variant="error">
                At least 1 terminal must be selected for the authorization.
              </Typography>
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Autocomplete
          disabled={R.equals('SINGLE', controlType)}
          getOptionLabel={R.prop('terminalShortName')}
          isOptionEqualToValue={R.eqProps('terminalId')}
          limitTags={3}
          loading={isFetchingTerminals}
          multiple
          onChange={handleChange}
          options={terminalsQuery}
          renderInput={renderInput({
            label: 'Search Terminals',
            length: terminalLength,
            loading: isFetchingTerminals,
          })}
          slotProps={{
            chip: {
              variant: 'selection',
            },
          }}
          value={terminals}
        />
      </AccordionDetails>
    </Accordion>
  );
};

Terminals.propTypes = {
  info: shape({
    customerIdName: string,
    terminals: arrayOf(shape({})),
  }),
  setInfo: func.isRequired,
};

Terminals.defaultProps = {
  info: {
    customerIdName: '',
    terminals: [],
  },
};

export default Terminals;
